import type { EstadoJogo } from './jogo';
import { clubesDaLiga, PAISES_MUNDO } from './ligas-mundo';
import { Rng } from './rng';

/**
 * Campeonatos internacionais: a cada virada de temporada o jogo simula, sem calendário na tela, a temporada de cada liga estrangeira
 * cadastrada (clubes e jogadores das planilhas; força do clube = média dos 11 melhores, ver ligas-mundo.ts). A classificação final
 * de cada uma fica em `e.ligasMundo` e aparece nas Tabelas (aba internacional). A da Argentina decide 4 vagas na Libertadores
 * seguinte (ver libertadores.ts).
 */
export interface LinhaMundo {
  clube: string;
  pts: number;
  v: number;
  e: number;
  d: number;
  gp: number;
  gc: number;
}

export interface TabelaMundo {
  /** temporada do jogo (1, 2, ...) em que a tabela foi simulada */
  temporada: number;
  tabela: LinhaMundo[];
}

/** Força de um clube sem valores na planilha (Série A da Itália): meio da faixa dos clubes que têm. */
const FORCA_PADRAO = 40;
const ESCALA_FORCA = 45;

function poisson(lambda: number, rng: Rng): number {
  const limite = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k++;
    p *= rng.proximo();
  } while (p > limite && k < 12);
  return k - 1;
}

/** Temporada de pontos corridos em dois turnos, com gols por Poisson a partir da diferença de força (mando dá um pouco de vantagem). */
export function simularLigaMundo(ligaId: string, rng: Rng): LinhaMundo[] {
  const clubes = clubesDaLiga(ligaId);
  const forca = new Map(clubes.map((c) => [c.nome, c.forca ?? FORCA_PADRAO]));
  const linhas = new Map<string, LinhaMundo>(clubes.map((c) => [c.nome, { clube: c.nome, pts: 0, v: 0, e: 0, d: 0, gp: 0, gc: 0 }]));
  const jogar = (casa: string, fora: string): void => {
    const dif = ((forca.get(casa) as number) - (forca.get(fora) as number)) / ESCALA_FORCA;
    const gc = poisson(1.4 * Math.exp(dif), rng);
    const gf = poisson(1.1 * Math.exp(-dif), rng);
    const a = linhas.get(casa) as LinhaMundo;
    const b = linhas.get(fora) as LinhaMundo;
    a.gp += gc;
    a.gc += gf;
    b.gp += gf;
    b.gc += gc;
    if (gc > gf) {
      a.v++;
      a.pts += 3;
      b.d++;
    } else if (gc < gf) {
      b.v++;
      b.pts += 3;
      a.d++;
    } else {
      a.e++;
      b.e++;
      a.pts++;
      b.pts++;
    }
  };
  for (const a of clubes) for (const b of clubes) if (a !== b) jogar(a.nome, b.nome);
  return [...linhas.values()].sort((x, y) => y.pts - x.pts || y.v - x.v || y.gp - y.gc - (x.gp - x.gc) || y.gp - x.gp || x.clube.localeCompare(y.clube, 'pt-BR'));
}

/**
 * Simula a temporada que termina em todas as ligas estrangeiras e guarda a classificação. Usa um gerador próprio (semente do jogo +
 * temporada), então não mexe na sequência de sorteios do resto do jogo.
 */
export function simularLigasMundo(e: EstadoJogo): void {
  const rng = new Rng((e.semente * 2654435761 + e.temporada * 40503) >>> 0);
  const ligas: Record<string, TabelaMundo> = {};
  for (const pais of PAISES_MUNDO) {
    for (const liga of pais.ligas) ligas[liga.id] = { temporada: e.temporada, tabela: simularLigaMundo(liga.id, rng) };
  }
  e.ligasMundo = ligas;
}

/** Os `n` primeiros da última temporada simulada da liga (nomes de clube); sem temporada simulada ainda, os mais fortes da planilha. */
export function classificadosDaLiga(e: EstadoJogo, ligaId: string, n: number): string[] {
  const simulada = e.ligasMundo?.[ligaId];
  if (simulada) return simulada.tabela.slice(0, n).map((l) => l.clube);
  return clubesDaLiga(ligaId)
    .filter((c) => c.forca !== undefined)
    .sort((a, b) => (b.forca as number) - (a.forca as number) || a.nome.localeCompare(b.nome, 'pt-BR'))
    .slice(0, n)
    .map((c) => c.nome);
}
